import { useState, useEffect } from 'react'
import { Form, Checkbox, Row, Col, Space } from 'antd'
import TimePicker from '../../common/TimePicker'
import DurationPicker from '../../common/DurationPicker'
import { Duration } from '../../common/durationModel'
import dayjs from 'dayjs'
import SettingsCard from '../SettingsCard'
import VSpace from '../../common/VSpace'
import { useTenantWorkDaySettingsDataQuery, useUpdateTenantWorkDaySettingsMutation } from '../../../domain/tenantWorkDaySettings/tenantWorkDaySettingsQueries'

type RouteSettingsData = {
  dayStart: dayjs.Dayjs
  anticipatedDayEnd: dayjs.Dayjs
  estimatedBreakEnabled: boolean
  estimatedBreakTime: Duration
}

const RouteSettings = () => {
  const [form] = Form.useForm()
  const { data: tenantWorkDaySettings, isLoading } = useTenantWorkDaySettingsDataQuery()
  const updateTenantWorkDaySettingsMutation = useUpdateTenantWorkDaySettingsMutation()

  const defaultSettings: RouteSettingsData = {
    dayStart: dayjs('08:00', 'HH:mm'),
    anticipatedDayEnd: dayjs('16:00', 'HH:mm'),
    estimatedBreakEnabled: true,
    estimatedBreakTime: { seconds: 30 * 60 },
  }

  const getSettingsFromApiData = (): RouteSettingsData | null => {
    if (tenantWorkDaySettings) {
      const workDayData = Array.isArray(tenantWorkDaySettings)
        ? tenantWorkDaySettings[0]
        : tenantWorkDaySettings

      if (workDayData) {
        const parseTimeFromDateTime = (datetimeString: string) => {
          const hour = parseInt(datetimeString.split('T')[1].split('+')[0].split('Z')[0].split(':')[0])
          const minute = parseInt(datetimeString.split('T')[1].split('+')[0].split('Z')[0].split(':')[1])
          if (isNaN(hour) || isNaN(minute)) {
            return null
          }
          return dayjs().hour(hour).minute(minute)
        }
        const dayStart = parseTimeFromDateTime(workDayData.dayStartTime)
        const anticipatedDayEnd = parseTimeFromDateTime(workDayData.dayEndTime)

        return {
          dayStart: dayStart || defaultSettings.dayStart,
          anticipatedDayEnd: anticipatedDayEnd || defaultSettings.anticipatedDayEnd,
          estimatedBreakEnabled: workDayData.hasBreakTime,
          estimatedBreakTime: { seconds: (workDayData.estimatedBreakTimeMinutes ?? 0) * 60 },
        }
      }
    }
    return null
  }

  const [initialSettings, setInitialSettings] = useState<RouteSettingsData | null>(null)
  const [settings, setSettings] = useState<RouteSettingsData>(defaultSettings)

  useEffect(() => {
    if (!isLoading && tenantWorkDaySettings) {
      const newSettings = getSettingsFromApiData()
      setInitialSettings(newSettings)
      const settingsToUse = newSettings || defaultSettings
      setSettings(settingsToUse)

      form.setFieldsValue({
        dayStart: settingsToUse.dayStart,
        anticipatedDayEnd: settingsToUse.anticipatedDayEnd,
      })
    }
  }, [tenantWorkDaySettings, isLoading, form])

  const isChanged = !settings.dayStart.isSame(initialSettings?.dayStart) ||
    !settings.anticipatedDayEnd.isSame(initialSettings?.anticipatedDayEnd) ||
    settings.estimatedBreakEnabled !== initialSettings?.estimatedBreakEnabled ||
    settings.estimatedBreakTime.seconds !== initialSettings?.estimatedBreakTime.seconds

  const handleUpdate = () => {
    updateTenantWorkDaySettingsMutation.mutate({
      dayStartTime: settings.dayStart.format('HH:mm:ss'),
      dayEndTime: settings.anticipatedDayEnd.format('HH:mm:ss'),
      hasBreakTime: settings.estimatedBreakEnabled,
      estimatedBreakTimeMinutes: Math.floor(settings.estimatedBreakTime.seconds / 60),
    })
  }

  return (
    <div style={{ margin: '15px' }}>
      <SettingsCard
        title="Work Day Settings"
        description="Manage default work day settings for all new routes. These settings will allow you to determine capacity on all routes."
        onUpdate={handleUpdate}
        isUpdating={updateTenantWorkDaySettingsMutation.isLoading}
        updateDisabled={!isChanged}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <VSpace>
            {/* Time pickers row */}
            <div style={{ display: 'flex', gap: 12 }}>
              <Form.Item
                label="Day Start"
                name="dayStart"
                rules={[{ required: true, message: 'Day start is required' }]}
              >
                <TimePicker
                  onChange={(value) => setSettings({ ...settings, dayStart: value })}
                />
              </Form.Item>
              <Form.Item
                label="Anticipated Day End"
                name="anticipatedDayEnd"
                rules={[{ required: true, message: 'Anticipated day end is required' }]}
              >
                <TimePicker
                  onChange={(value) => setSettings({ ...settings, anticipatedDayEnd: value })}
                />
              </Form.Item>
            </div>

            {/* Estimated Break Time row */}
            <Row>
              <Col span={24}>
                <Space align="center" size="middle">
                  <Checkbox
                    checked={settings.estimatedBreakEnabled}
                    onChange={(e) => setSettings({ ...settings, estimatedBreakEnabled: e.target.checked })}
                  >
                    <span style={{ fontWeight: 500 }}>
                      Estimated Break Time
                    </span>
                  </Checkbox>
                  {settings.estimatedBreakEnabled && (
                    <DurationPicker
                      value={settings.estimatedBreakTime}
                      onChange={(d) => setSettings({ ...settings, estimatedBreakTime: d })}
                      increment={300}
                    />
                  )}
                </Space>
              </Col>
            </Row>
          </VSpace>
        </Form>
      </SettingsCard>
    </div>
  )
}

export default RouteSettings